import { FC } from 'react';
import { Tooltip as UITooltip } from '@/components/ui';
import { Calendar } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';
import { TabKey, SxmBooking, MxmLead, MarketingKPIs } from '../types';
import { PlatformConnectionsCard } from './PlatformConnectionsCard';
import { SkeletonList, SkeletonQuickStats } from './DashboardSkeletons';

import { RecentLeadsList } from './RecentLeadsList';

interface DashboardSidebarProps {
    loading: boolean;
    activeTab: TabKey;
    leads: MxmLead[];
    bookings: SxmBooking[];
    marketingKPIs: MarketingKPIs | null;
    upcomingBookings: SxmBooking[];
    bookingsByStatus: Array<{ name: string; value: number; color: string }>;
    recentLeads: MxmLead[];
}

export const DashboardSidebar: FC<DashboardSidebarProps> = ({
    loading,
    activeTab,
    leads,
    bookings,
    marketingKPIs,
    upcomingBookings,
    bookingsByStatus,
    recentLeads
}) => {
    if (loading) {
        return (
            <aside className="col-span-12 xl:col-span-3 space-y-6">
                <SkeletonQuickStats />
                <SkeletonList />
                <SkeletonList />
            </aside>
        );
    }

    return (
        <aside className="col-span-12 xl:col-span-3 space-y-6">
            <div className="bg-slate-900 text-white rounded-2xl p-5">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-2xl font-bold">{leads.length}</p>
                        <p className="text-[10px] text-slate-400">Leads</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold">{bookings.length}</p>
                        <p className="text-[10px] text-slate-400">Bookings</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold">{leads.filter(l => l.status === 'booked').length}</p>
                        <p className="text-[10px] text-slate-400">Converted</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold">{marketingKPIs?.publishedContent || 0}</p>
                        <p className="text-[10px] text-slate-400">Content</p>
                    </div>
                </div>
            </div>

            {activeTab === 'sales' && (
                <div className="bg-white rounded-2xl p-6 border border-slate-100 min-h-[240px] flex flex-col">
                    <UITooltip content="Your upcoming scheduled meetings." position="left">
                        <div className="flex items-center gap-2 mb-4">
                            <Calendar className="w-4 h-4 text-emerald-500" />
                            <h3 className="text-xs font-semibold text-slate-800">Upcoming Bookings</h3>
                        </div>
                    </UITooltip>
                    {upcomingBookings.length > 0 ? (
                        <div className="space-y-3">
                            {upcomingBookings.map((b) => (
                                <div key={b.id} className="p-3 bg-slate-50 rounded-xl">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-slate-800">
                                            {format(parseISO(`${b.date}T${b.time}`), 'iii, MMM dd')}
                                        </p>
                                        <p className="text-[10px] font-bold text-indigo-600">{b.time}</p>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1 truncate">{b.lead?.email || 'Lead'}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center py-6">
                            <p className="text-xs text-slate-300">No upcoming bookings</p>
                        </div>
                    )}
                </div>
            )}

            <PlatformConnectionsCard />

            {activeTab === 'sales' && (
                <RecentLeadsList leads={leads} recentLeads={recentLeads} />
            )}

            {activeTab === 'sales' && bookingsByStatus.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <UITooltip content="A breakdown of your meetings by their current status (Confirmed, Canceled, etc)." position="left">
                        <h3 className="text-xs font-semibold text-slate-800 mb-4">Booking Status</h3>
                    </UITooltip>
                    <div className="h-[130px] relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={bookingsByStatus}
                                    innerRadius={38}
                                    outerRadius={52}
                                    paddingAngle={3}
                                    dataKey="value"
                                    strokeWidth={0}
                                >
                                    {bookingsByStatus.map((e, i) => (
                                        <Cell key={i} fill={e.color} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-xl font-bold text-slate-900">{bookings.length}</span>
                            <span className="text-[10px] text-slate-400">total</span>
                        </div>
                    </div>
                    <div className="flex justify-center gap-4 mt-3">
                        {bookingsByStatus.map((item) => (
                            <div key={item.name} className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                                <span className="text-[10px] text-slate-500 capitalize">{item.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </aside>
    );
};
